import React, {useMemo, useRef, useState} from "react";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {ProgressBar} from "primereact/progressbar";
import {RadioButton} from "primereact/radiobutton";
import {FIELD_TYPES, fieldError, formState, normalizeValue, safeRedirectUrl} from "./engine";
import {fieldErrorText} from "./forms-api";
import {DROPDOWN_PT, Icon, PrimeDatePicker, useFormat, useT} from "./host";
import {themeOf} from "./forms-ui";

// One form as a respondent sees it: the public page (/f/<token>), the builder preview and test answers.
// Rules (show / hide / required / end) run with the same engine as the server, which checks everything again.
const answerable = (field) => Boolean(FIELD_TYPES[field.type]?.answer);
const INPUT = "w-full text-sm border-round-lg";
const toSeconds = (iso) => (iso ? Math.floor(new Date(iso).getTime() / 1000) : null);

function FileInput({field, value, onChange, upload, disabled}) {
    const t = useT();
    const input = useRef(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const files = Array.isArray(value) ? value : [];
    const max = Number(field.validation?.maxFiles) || 3;
    return (
        <div className="flex flex-column gap-2">
            {files.map((file) => (
                <div key={file.id} className="flex align-items-center gap-2 border-1 border-200 border-round-lg px-2 py-1 text-xs">
                    <Icon icon="mi mi:attach_file" className="text-base text-500"/>
                    <span className="flex-1 overflow-hidden text-overflow-ellipsis white-space-nowrap">{file.name}</span>
                    {!disabled && <Button type="button" text rounded severity="danger" className="p-1" icon={<Icon icon="pi pi-times" className="text-sm"/>} aria-label={t("forms.public.remove-file")} onClick={() => onChange(files.filter((item) => item.id !== file.id))}/>}
                </div>
            ))}
            {files.length < max && (
                <>
                    <input ref={input} type="file" hidden onChange={async (event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        if (!file) return;
                        setBusy(true);
                        setError("");
                        try {
                            onChange([...files, await upload(field.key, file)]);
                        } catch (uploadError) {
                            setError(uploadError?.message || t("forms.error.upload"));
                        } finally {
                            setBusy(false);
                        }
                    }}/>
                    <Button type="button" outlined className="text-xs gap-2 border-round-lg align-self-start" icon={<Icon icon="mi mi:upload" className="text-base"/>} loading={busy}
                            disabled={disabled || !upload} label={upload ? t("forms.public.choose-file") : t("forms.public.upload-preview-off")} onClick={() => input.current?.click()}/>
                </>
            )}
            {error && <small className="text-red-500 text-xs">{error}</small>}
        </div>
    );
}

// the label wraps the control's own input, so a click anywhere on the row selects it (once)
function ChoiceRow({selected, theme, children}) {
    return (
        <label className={`flex align-items-center gap-2 border-1 border-round-lg p-2 text-sm cursor-pointer ${selected ? `${theme?.accent || "border-primary"} ${theme?.tint || "surface-50"}` : "border-200"}`}>
            {children}
        </label>
    );
}

// one question's input; also the builder canvas's live preview of a question
export function FieldInput({field, value, onChange, invalid, disabled, upload, theme}) {
    const t = useT();
    const format = useFormat();
    const id = `sf-${field.key}`;
    switch (field.type) {
        case "long_text":
            return <InputTextarea id={id} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={4} autoResize className={INPUT} placeholder={field.placeholder} invalid={invalid} disabled={disabled}/>;
        case "email":
            return <InputText id={id} dir="ltr" type="email" value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${INPUT} h-3rem`} placeholder={field.placeholder || "name@example.com"} invalid={invalid} disabled={disabled}/>;
        case "phone":
            return <InputText id={id} inputMode="tel" value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${INPUT} h-3rem`} placeholder={field.placeholder || t("forms.public.phone-placeholder")} invalid={invalid} disabled={disabled}/>;
        case "number":
        case "amount":
            return <InputText id={id} inputMode="decimal" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${INPUT} h-3rem`} placeholder={field.placeholder} invalid={invalid} disabled={disabled}/>;
        case "date":
        case "datetime":
            return <PrimeDatePicker inputClassName="w-full" value={toSeconds(value)} placeholder={field.placeholder || t("forms.public.pick-date")} onChange={(e) => onChange(e.value ? new Date(e.value * 1000).toISOString() : null)}/>;
        case "dropdown":
            return <Dropdown inputId={id} value={value ?? null} options={field.options || []} optionLabel="label" optionValue="value" onChange={(e) => onChange(e.value)} className="w-full text-sm h-3rem border-round-lg" panelClassName="rtl text-xs" pt={DROPDOWN_PT} placeholder={field.placeholder || t("forms.public.choose")} invalid={invalid} disabled={disabled}/>;
        case "single_choice":
            return (
                <div className="flex flex-column gap-2">
                    {(field.options || []).map((option) => (
                        <ChoiceRow key={option.value} theme={theme} selected={value === option.value}>
                            <RadioButton inputId={`${id}-${option.value}`} value={option.value} checked={value === option.value} onChange={() => onChange(option.value)} disabled={disabled}/>
                            <span>{option.label}</span>
                        </ChoiceRow>
                    ))}
                </div>
            );
        case "multi_choice": {
            const list = Array.isArray(value) ? value : [];
            const toggle = (option) => onChange(list.includes(option.value) ? list.filter((item) => item !== option.value) : [...list, option.value]);
            return (
                <div className="flex flex-column gap-2">
                    {(field.options || []).map((option) => (
                        <ChoiceRow key={option.value} theme={theme} selected={list.includes(option.value)}>
                            <Checkbox checked={list.includes(option.value)} disabled={disabled} onChange={() => toggle(option)}/>
                            <span>{option.label}</span>
                        </ChoiceRow>
                    ))}
                </div>
            );
        }
        case "yes_no":
            return (
                <div className="flex gap-2">
                    {["yes", "no"].map((key) => (
                        <Button key={key} type="button" className="text-xs border-round-lg flex-1 justify-content-center" label={t(`forms.answer.${key}`)} severity={theme?.severity} outlined={value !== key} disabled={disabled} onClick={() => onChange(key)}/>
                    ))}
                </div>
            );
        case "rating": {
            const scale = Number(field.scale) || 5;
            return (
                <div className="flex flex-column gap-1">
                    <div className="flex gap-1 flex-wrap">
                        {Array.from({length: scale}, (_, index) => index + 1).map((number) => (
                            <Button key={number} type="button" className="text-xs border-round-lg w-3rem justify-content-center" label={format.number(number)} severity={theme?.severity} outlined={Number(value) !== number} disabled={disabled} onClick={() => onChange(number)}/>
                        ))}
                    </div>
                    {field.scaleLabels?.length === 2 && <div className="flex justify-content-between text-xs text-500"><span>{field.scaleLabels[0]}</span><span>{field.scaleLabels[1]}</span></div>}
                </div>
            );
        }
        case "consent":
            return (
                <label className="flex align-items-start gap-2 cursor-pointer text-sm">
                    <Checkbox checked={Boolean(value)} onChange={(e) => onChange(e.checked)} disabled={disabled}/>
                    <span className="line-height-3">{field.consentText || field.label}</span>
                </label>
            );
        case "file":
            return <FileInput field={field} value={value} onChange={onChange} upload={upload} disabled={disabled}/>;
        default:
            return <InputText id={id} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${INPUT} h-3rem`} placeholder={field.placeholder} invalid={invalid} disabled={disabled}/>;
    }
}

export function EndingView({ending, onRestart}) {
    const t = useT();
    const redirect = ending?.type === "redirect" ? safeRedirectUrl(ending.url) : null;
    const button = ending?.buttonLabel ? safeRedirectUrl(ending.buttonUrl) : null;
    return (
        <div className="flex flex-column align-items-center text-center gap-3 py-5 px-3">
            <span className="bg-green-50 border-circle p-3 flex align-items-center justify-content-center"><Icon icon="mi mi:check_circle" className="text-5xl text-green-500"/></span>
            <h2 className="m-0 text-xl">{ending?.title || t("forms.ending.default-title")}</h2>
            <p className="m-0 text-sm text-600 line-height-3 white-space-pre-line">{ending?.message || t("forms.ending.default-message")}</p>
            {ending?.tracking && <span className="text-xs text-500">{t("forms.ending.tracking")} <strong dir="ltr">{ending.tracking}</strong></span>}
            <div className="flex flex-wrap gap-2 justify-content-center">
                {redirect && <Button type="button" className="text-xs gap-2" icon={<Icon icon="mi mi:open_in_new" className="text-base"/>} label={t("forms.ending.continue")} onClick={() => window.open(redirect, ending.newTab ? "_blank" : "_self", "noopener")}/>}
                {button && <Button type="button" outlined className="text-xs gap-2" icon={<Icon icon="mi mi:open_in_new" className="text-base"/>} label={ending.buttonLabel} onClick={() => window.open(button, "_blank", "noopener")}/>}
                {onRestart && <Button type="button" text className="text-xs gap-2" icon={<Icon icon="mi mi:replay" className="text-base"/>} label={t("forms.ending.again")} onClick={onRestart}/>}
            </div>
        </div>
    );
}

// definition: the (public) definition; onSubmit(answers) → resolves; serverErrors: {fieldKey: {code, n}}
export default function FormRenderer({definition, onSubmit, upload, submitting, serverErrors = {}, disabled, submitLabel}) {
    const t = useT();
    const [answers, setAnswers] = useState({});
    const [touched, setTouched] = useState(false);
    const context = useMemo(() => ({now: Date.now(), system: {source: "public"}}), []);
    const fields = useMemo(() => definition?.fields || [], [definition]);
    // the engine reads normalized values (Persian digits 0912… → +98912…, "12" → 12), as on the server
    const normalized = useMemo(() => {
        const out = {};
        fields.forEach((field) => {
            if (!answerable(field)) return;
            const value = normalizeValue(field, answers[field.key]);
            if (value !== null && value !== undefined) out[field.key] = value;
        });
        return out;
    }, [fields, answers]);
    const state = useMemo(() => formState(definition, normalized, context), [definition, normalized, context]);
    const problem = (field) => fieldError(field, normalized[field.key], state.required[field.key]);
    const meta = definition?.meta || {};
    const theme = themeOf(meta);
    const errors = {};
    if (touched) {
        fields.forEach((field) => {
            if (!answerable(field) || !state.visible[field.key]) return;
            const error = problem(field);
            if (error) errors[field.key] = error;
        });
    }
    const allErrors = {...errors, ...serverErrors};
    const visibleFields = fields.filter((field) => state.visible[field.key]);
    const answerableVisible = visibleFields.filter(answerable);
    const answered = answerableVisible.filter((field) => normalized[field.key] !== undefined).length;

    const submit = (event) => {
        event.preventDefault();
        setTouched(true);
        if (fields.some((field) => answerable(field) && state.visible[field.key] && problem(field))) return;
        const out = {};
        fields.forEach((field) => {
            if (answerable(field) && state.visible[field.key] && answers[field.key] !== undefined) out[field.key] = answers[field.key];
        });
        onSubmit?.(out);
    };

    return (
        <form dir="rtl" className="flex flex-column gap-4" onSubmit={submit} noValidate>
            <div className="flex flex-column gap-2">
                {meta.brandName && <span className="text-xs font-bold text-600">{meta.brandName}</span>}
                <h1 className="m-0 text-2xl">{meta.title || t("forms.untitled")}</h1>
                {meta.description && <p className="m-0 text-sm text-600 line-height-3 white-space-pre-line">{meta.description}</p>}
                {meta.showProgress && answerableVisible.length > 0 && (
                    <ProgressBar value={Math.round((answered / answerableVisible.length) * 100)} showValue={false} className="h-1rem border-round-3xl"/>
                )}
            </div>
            {visibleFields.map((field) => {
                if (field.type === "heading") return <h3 key={field.key} className="m-0 text-base text-800">{field.label}</h3>;
                if (field.type === "paragraph") return <p key={field.key} className="m-0 text-sm text-600 line-height-3 white-space-pre-line">{field.label}</p>;
                if (field.type === "divider") return <div key={field.key} className="border-top-1 border-200"/>;
                if (field.type === "hidden") return null;
                const required = state.required[field.key];
                return (
                    <div key={field.key} className="flex flex-column gap-2">
                        {field.type !== "consent" && (
                            <label htmlFor={`sf-${field.key}`} className="text-sm font-bold text-800">
                                {field.label}{required && <span className="text-red-500"> *</span>}
                            </label>
                        )}
                        {field.help && <span className="text-xs text-500 line-height-3">{field.help}</span>}
                        <FieldInput field={field} value={answers[field.key]} disabled={disabled} upload={upload} theme={theme} invalid={Boolean(allErrors[field.key])}
                                    onChange={(value) => setAnswers((current) => ({...current, [field.key]: value}))}/>
                        {allErrors[field.key] && <small className="text-red-500 text-xs">{fieldErrorText(t, allErrors[field.key])}</small>}
                    </div>
                );
            })}
            {state.ended && <div className="surface-50 border-round-lg p-2 text-xs text-primary">{t("forms.public.ends-here")}</div>}
            <Button type="submit" severity={theme.severity} className="text-sm gap-2 border-round-lg align-self-start px-5" icon={<Icon icon="mi mi:send" className="text-base"/>}
                    label={submitLabel || t("forms.public.submit")} loading={submitting} disabled={disabled}/>
        </form>
    );
}
